import cn from 'classnames';
import { useEffect, useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import Heading from 'components/common/Heading';
import Text from 'components/common/Text';
import Loading from 'components/icons/Loading';
import {
  ProgressBarContext,
  UserContext,
  PickYourMealsContext,
} from 'components/signUpFunnel/Contexts';
import LegalCopy from 'components/signUpFunnel/LegalCopy';
import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { TEMPO } from 'lib/constants';
import { createEvents } from 'services/RequestService/endpoints/signUp';
import styles from 'styles/components/signUpFunnel/thankYou/ThankYouContainer';

import InviteFriendsInstructions from './InviteFriendsInstructions';
import NextSteps from './NextSteps';
import PickYourMealsHero from './PickYourMealsHero';
import ShareInvitations from './ShareInvitations';

const ThankYouContainer = () => {
  const { setProgress } = useContext(ProgressBarContext);
  const { user } = useContext(UserContext);
  const { brand } = useContext(BrandContext);
  const { isPickYourMeals } = useContext(PickYourMealsContext);
  const { isMobile: isSmartphoneScreenSize, isMobileOrTablet } = useScreenSizeConstants();
  const mealPlanSlug = useFunnelMealPlan();

  const firstName = useMemo(() => user?.firstName, [user]);

  const PickYourMealsGreeting = () => (
    <div className="flex flex-col items-center">
      <Text brandStylesOverrides="h1 text-tempo-black-100">Nicely done!</Text>
      <Text brandStylesOverrides="h1 text-tempo-black-100">Here’s what’s next:</Text>
    </div>
  );

  useMetadata({ title: `Step 6: Thank You | ${brandData(brand)?.displayName}` }, false);

  const onContinue = useAnalyticsCallback(
    null,
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug || TEMPO,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug,
          });
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals]
  );

  useAnalyticsEffect(
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.viewed, {
            mealPlanSlug: TEMPO,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.viewed);
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals]
  );

  useEffect(() => {
    setProgress({
      percent: '76',
      stepNumber: '5',
      totalSteps: '5',
      nextStep: 'Pick your meals',
    });

    createEvents({ params: { event_name: 'PageView' } });
  }, []);

  const DefaultUserGreeting = () => {
    return (
      <Heading
        className={cn('mx-auto items-center px-4 pb-6 pt-12 text-center bp1:px-0', {
          'bg-cream-110': brand !== TEMPO,
          'bg-tempo-gray-100': brand === TEMPO,
        })}
        brandStylesOverrides="h1"
      >
        {isPickYourMeals ? (
          <PickYourMealsGreeting />
        ) : firstName ? (
          `Welcome, ${firstName}!`
        ) : (
          <Skeleton count={2} className="px-4" />
        )}
      </Heading>
    );
  };

  const InvitationSection = () => {
    if (isMobileOrTablet) {
      return (
        <section>
          <ShareInvitations />
          <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
        </section>
      );
    } else {
      return (
        <div className="flex w-full flex-row">
          <div className="w-1/2">
            <ShareInvitations />
          </div>
          <div className="w-1/2">
            <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
          </div>
        </div>
      );
    }
  };

  const SmartphoneUserGreeting = () => {
    return (
      <Heading
        className={cn('items-center px-4 py-6 text-center text-charcoal-100 bp1:px-0 bp2:mx-auto', {
          'bg-cream-110': brand !== TEMPO,
          'bg-tempo-gray-100': brand === TEMPO,
        })}
        brandStylesOverrides="h1"
      >
        {isPickYourMeals ? (
          <PickYourMealsGreeting />
        ) : firstName ? (
          `Welcome, ${firstName}!`
        ) : (
          "We're glad you've joined us!"
        )}
      </Heading>
    );
  };

  const SmartphoneInvitationSection = () => {
    return (
      <section
        className={cn(styles.smartphoneInvitationSection, styles[brand], 'relative flex flex-col')}
      >
        <ShareInvitations isSmartphoneScreenSize={isSmartphoneScreenSize} />
        <InviteFriendsInstructions
          isSmartphoneScreenSize={isSmartphoneScreenSize}
          isPickYourMeals={isPickYourMeals}
        />
      </section>
    );
  };

  const SmartphoneThankYouPage = () => {
    return (
      <section
        className={cn(styles.smartphoneThankYouPage, styles[brand], {
          'bg-cream-100': brand !== TEMPO && !isPickYourMeals,
          'bg-tempo-gray-40': brand === TEMPO,
        })}
      >
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : (
          <>
            <SmartphoneUserGreeting />
            <NextSteps onClick={onContinue} isSmartphoneScreenSize={isSmartphoneScreenSize} />
          </>
        )}
        <SmartphoneInvitationSection />
        <LegalCopy onClick={onContinue} isSmartphoneScreenSize={isSmartphoneScreenSize} />
      </section>
    );
  };

  const DefaultThankYouPage = () => {
    return (
      <section className={cn({ 'bg-tempo-gray-40': isPickYourMeals })}>
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : (
          <>
            <DefaultUserGreeting />
            <NextSteps onClick={onContinue} />
          </>
        )}
        <InvitationSection />
        <LegalCopy onClick={onContinue} />
      </section>
    );
  };

  return user ? (
    isSmartphoneScreenSize ? (
      <SmartphoneThankYouPage />
    ) : (
      <DefaultThankYouPage />
    )
  ) : (
    <Loading />
  );
};

export default ThankYouContainer;
